import React, { useState, useEffect } from 'react';
import { IndianRupee, Pencil, Trash2, Lock, X } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_API_URL}/records`;
const NUMBER_LINKS_API_URL = `${process.env.REACT_APP_API_URL}/number-links`;
const EDIT_WINDOW_MS = 10 * 60 * 1000; // 10 minutes

function getTimeLeft(createdAt) {
  const elapsed = Date.now() - new Date(createdAt).getTime();
  const remaining = EDIT_WINDOW_MS - elapsed;
  if (remaining <= 0) return null;
  const mins = Math.floor(remaining / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);
  return `${mins}m ${secs}s`;
}

function isEditable(createdAt) {
  return (Date.now() - new Date(createdAt).getTime()) <= EDIT_WINDOW_MS;
}

// ---- Edit Modal Component ----
const EditModal = ({ record, onClose, onSaved }) => {
  const [formData, setFormData] = useState({
    customer_name: record.customer_name || '',
    person_name: record.person_name || '',
    mobile_number: record.mobile_number || '',
    vehicle_number: record.vehicle_number || '',
    vehicle_type: record.vehicle_type || 'bike',
    fuel_type: record.fuel_type || 'petrol',
    puc_expire_tenure: record.puc_expire_tenure || 6,
    amount: record.amount || '',
    payment_method: record.payment_method || 'cash',
    type: record.type || 'Number Link',
    record_date: new Date(record.record_date).toLocaleDateString('en-CA'),
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const endpoint = record.category === 'PUC' ? API_URL : NUMBER_LINKS_API_URL;
    try {
      await axios.put(`${endpoint}/${record.id}`, formData);
      onSaved('Record updated successfully!');
      onClose();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to update record';
      onSaved(msg, true);
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Edit Record</h3>
          <button className="modal-close" onClick={onClose}><X size={20}/></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-grid">
            {record.category === 'PUC' ? (
              <>
                <div className="form-group">
                  <label>Customer Name</label>
                  <input type="text" name="customer_name" className="form-control" value={formData.customer_name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Mobile Number</label>
                  <input type="tel" name="mobile_number" className="form-control" value={formData.mobile_number} onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    if (val.length <= 10) handleChange({ target: { name: 'mobile_number', value: val } });
                  }} pattern="[0-9]{10}" maxLength="10" minLength="10" title="Must be exactly 10 digits" required />
                </div>
                <div className="form-group">
                  <label>Vehicle Number</label>
                  <input type="text" name="vehicle_number" className="form-control" value={formData.vehicle_number} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Vehicle Type</label>
                  <select name="vehicle_type" className="form-control" value={formData.vehicle_type} onChange={handleChange}>
                    <option value="bike">Bike</option>
                    <option value="car">Car</option>
                    <option value="3 wheeler">3 Wheeler</option>
                    <option value="truck">Truck</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Fuel Type</label>
                  <select name="fuel_type" className="form-control" value={formData.fuel_type} onChange={handleChange}>
                    <option value="petrol">Petrol</option>
                    <option value="diesel">Diesel</option>
                    <option value="cng">CNG</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>PUC Tenure</label>
                  <select name="puc_expire_tenure" className="form-control" value={formData.puc_expire_tenure} onChange={handleChange}>
                    <option value="3">3 Months</option>
                    <option value="6">6 Months</option>
                    <option value="12">12 Months</option>
                  </select>
                </div>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label>Service Type</label>
                  <select name="type" className="form-control" value={formData.type || 'Number Link'} onChange={handleChange}>
                    <option value="Number Link">Number Link</option>
                    <option value="PUC Reprint">PUC Reprint</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{formData.type === 'PUC Reprint' ? 'Customer / Vehicle Name' : 'Person Name'}</label>
                  <input type="text" name="person_name" className="form-control" value={formData.person_name} onChange={handleChange} required />
                </div>
              </>
            )}
            
            <div className="form-group">
              <label>Amount (₹)</label>
              <input type="number" name="amount" className="form-control" value={formData.amount} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Payment Method</label>
              <select name="payment_method" className="form-control" value={formData.payment_method || 'cash'} onChange={handleChange}>
                <option value="cash">Cash</option>
                <option value="paytm">Paytm / Online</option>
              </select>
            </div>
            <div className="form-group">
              <label>Date</label>
              <input type="date" name="record_date" className="form-control" value={formData.record_date} onChange={handleChange} required />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">Save Changes</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---- Main DailyBook Component ----
const DailyBook = ({ records, numberLinks = [], token, role, onRecordChanged }) => {
  const today = new Date().toLocaleDateString('en-CA');
  // eslint-disable-next-line no-unused-vars
  const [tick, setTick] = useState(0);
  const [editRecord, setEditRecord] = useState(null);
  const [showShiftClose, setShowShiftClose] = useState(false);
  const [isShiftClosed, setIsShiftClosed] = useState(false);

  // Re-render every second so countdown timers update live
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const checkShiftStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL.replace('/records', '')}/shift/${today}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsShiftClosed(res.data.is_closed);
      } catch (err) {
        console.error('Failed to fetch shift status', err);
      }
    };
    checkShiftStatus();
  }, [today]);

  const allRecords = [
    ...records.map(r => ({ ...r, category: 'PUC' })),
    ...numberLinks.map(r => ({ ...r, category: 'Number Link' }))
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const todaysRecords = allRecords.filter(record => {
    const recordDate = new Date(record.record_date).toLocaleDateString('en-CA');
    return recordDate === today;
  });

  const totalCollection = todaysRecords.reduce((acc, record) => acc + Number(record.amount), 0);
  
  const totalCash = todaysRecords.filter(r => r.payment_method === 'cash' || !r.payment_method).reduce((acc, r) => acc + Number(r.amount), 0);
  const totalPaytm = todaysRecords.filter(r => r.payment_method === 'paytm').reduce((acc, r) => acc + Number(r.amount), 0);
  
  const totalDiesel = todaysRecords.filter(r => r.fuel_type === 'diesel').length;
  const totalPetrol = todaysRecords.filter(r => r.fuel_type === 'petrol').length;
  const totalCNG = todaysRecords.filter(r => r.fuel_type === 'cng').length;

  const toggleShiftLock = async () => {
    try {
      const token = localStorage.getItem('token');
      const newState = !isShiftClosed;
      await axios.post(`${API_URL.replace('/records', '')}/shift/toggle`, 
        { date: today, is_closed: newState },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsShiftClosed(newState);
      onRecordChanged(newState ? 'Shift Closed!' : 'Shift Re-Opened!');
    } catch (err) {
      alert('Failed to update shift status');
    }
  };

  const copySummaryToClipboard = () => {
    const totalNumberLinksOnly = todaysRecords.filter(r => r.category === 'Number Link' && r.type !== 'PUC Reprint').reduce((acc, r) => acc + Number(r.amount), 0);
    const totalPUCReprints = todaysRecords.filter(r => r.category === 'Number Link' && r.type === 'PUC Reprint').reduce((acc, r) => acc + Number(r.amount), 0);
    const totalPUC = totalCollection - totalNumberLinksOnly - totalPUCReprints;
    
    const text = `*Shift Closing Summary (${new Date().toLocaleDateString('en-GB')})*\n\n` +
      `💰 *Total Collected:* ₹${totalCollection.toFixed(2)}\n\n` +
      `💵 *Cash:* ₹${totalCash.toFixed(2)}\n` +
      `📱 *Paytm:* ₹${totalPaytm.toFixed(2)}\n\n` +
      `📄 *PUC Collection:* ₹${totalPUC.toFixed(2)}\n` +
      `🔗 *Number Link Collection:* ₹${totalNumberLinksOnly.toFixed(2)}\n` +
      `🖨️ *PUC Reprint Collection:* ₹${totalPUCReprints.toFixed(2)}\n\n` +
      `🚗 *Vehicle Breakdown*\n` +
      `- Petrol: ${totalPetrol}\n` +
      `- Diesel: ${totalDiesel}\n` +
      `- CNG: ${totalCNG}\n\n` +
      `Status: ${isShiftClosed ? '🔒 CLOSED' : '🔓 OPEN'}`;
      
    navigator.clipboard.writeText(text).then(() => {
      alert('Summary copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy to clipboard. Please copy manually.');
    });
  };

  const handleDelete = async (record) => {
    const name = record.category === 'PUC' ? record.customer_name : record.person_name;
    if (!window.confirm(`Delete ${record.category} record for ${name}?`)) return;
    try {
      const endpoint = record.category === 'PUC' ? API_URL : NUMBER_LINKS_API_URL;
      await axios.delete(`${endpoint}/${record.id}`);
      onRecordChanged('Record deleted successfully!');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to delete record';
      onRecordChanged(msg, true);
    }
  };

  return (
    <div>
      {editRecord && (
        <EditModal
          record={editRecord}
          onClose={() => setEditRecord(null)}
          onSaved={(msg, isError) => {
            onRecordChanged(msg, isError);
          }}
        />
      )}

      {showShiftClose && (
        <div className="modal-overlay" onClick={() => setShowShiftClose(false)}>
          <div className="modal-box" style={{ maxWidth: '420px', padding: '1.25rem', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid var(--card-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>Shift Closing Summary</h3>
              <button className="action-btn" onClick={() => setShowShiftClose(false)}>✕</button>
            </div>
            
            <div className="modal-body">
              {/* Grand Total Highlight */}
              <div style={{ background: 'rgba(59,130,246,0.1)', padding: '0.75rem', borderRadius: '12px', textAlign: 'center', marginBottom: '0.75rem', border: '1px solid rgba(59,130,246,0.2)' }}>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Total Collected Money</div>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--primary)', lineHeight: 1 }}>₹{totalCollection.toFixed(2)}</div>
              </div>
              
              {/* Split Metrics */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div style={{ background: 'var(--card-bg)', padding: '0.75rem', borderRadius: '10px', border: '1px solid var(--card-border)', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.25rem' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ef4444' }}></div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>CASH</div>
                  </div>
                  <div style={{ fontSize: '1.35rem', fontWeight: 700, color: '#ef4444' }}>₹{totalCash.toFixed(2)}</div>
                </div>
                
                <div style={{ background: 'var(--card-bg)', padding: '0.75rem', borderRadius: '10px', border: '1px solid var(--card-border)', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.25rem' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981' }}></div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>PAYTM</div>
                  </div>
                  <div style={{ fontSize: '1.35rem', fontWeight: 700, color: '#10b981' }}>₹{totalPaytm.toFixed(2)}</div>
                </div>
              </div>

              {/* Breakdown Cards Stacked Vertically */}
              
              {/* PUC Vehicle Breakdown */}
              <div style={{ background: 'var(--card-bg)', padding: '0.75rem', borderRadius: '10px', border: '1px solid var(--card-border)', marginBottom: '0.75rem' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: 'var(--text-main)', fontWeight: 600 }}>PUC Vehicle Breakdown</h4>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.25rem', borderBottom: '1px solid var(--input-border)', fontSize: '0.8rem' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Petrol Vehicles</span>
                  <span style={{ fontWeight: 700, color: '#ef4444' }}>{totalPetrol}</span>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0', borderBottom: '1px solid var(--input-border)', fontSize: '0.8rem' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Diesel Vehicles</span>
                  <span style={{ fontWeight: 700, color: '#3b82f6' }}>{totalDiesel}</span>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.25rem', fontSize: '0.8rem' }}>
                  <span style={{ color: 'var(--text-muted)' }}>CNG Vehicles</span>
                  <span style={{ fontWeight: 700, color: '#22c55e' }}>{totalCNG}</span>
                </div>
              </div>

              {/* Other Services Breakdown */}
              <div style={{ background: 'var(--card-bg)', padding: '0.75rem', borderRadius: '10px', border: '1px solid var(--card-border)', marginBottom: '0.75rem' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: 'var(--text-main)', fontWeight: 600 }}>Other Income Breakdown</h4>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.25rem', borderBottom: '1px solid var(--input-border)', fontSize: '0.8rem' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Number Links</span>
                  <span style={{ fontWeight: 700, color: 'var(--primary)' }}>₹{todaysRecords.filter(r => r.category === 'Number Link' && r.type !== 'PUC Reprint').reduce((acc, r) => acc + Number(r.amount), 0).toFixed(2)}</span>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.25rem', fontSize: '0.8rem' }}>
                  <span style={{ color: 'var(--text-muted)' }}>PUC Reprints</span>
                  <span style={{ fontWeight: 700, color: '#10b981' }}>₹{todaysRecords.filter(r => r.category === 'Number Link' && r.type === 'PUC Reprint').reduce((acc, r) => acc + Number(r.amount), 0).toFixed(2)}</span>
                </div>
              </div>

            </div>
            
            {/* Buttons Stacked Vertically */}
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
              {isShiftClosed && (
                <button 
                  onClick={copySummaryToClipboard} 
                  style={{ width: '100%', padding: '0.75rem', fontSize: '0.95rem', fontWeight: 600, background: 'var(--primary)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Generate WhatsApp Message
                </button>
              )}
              
              <button 
                onClick={toggleShiftLock} 
                style={{ width: '100%', padding: '0.75rem', fontSize: '0.95rem', fontWeight: 600, background: isShiftClosed ? '#3b82f6' : '#ef4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
              >
                {isShiftClosed ? '🔓 Re-Open' : '🔒 Lock & Close'}
              </button>
              
              <button 
                onClick={() => setShowShiftClose(false)} 
                style={{ width: '100%', padding: '0.75rem', fontSize: '0.95rem', fontWeight: 600, background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--card-border)', borderRadius: '8px', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Metric Widget */}
      <div className="dash-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div className="metric-widget">
          <div className="metric-icon">
            <IndianRupee size={32} />
          </div>
          <div>
            <div className="metric-value">₹{totalCollection.toFixed(2)}</div>
            <div className="metric-label">Total Collection Today ({todaysRecords.length} entries)</div>
          </div>
        </div>
        <button 
          className="btn-primary" 
          style={{ background: '#ef4444', borderColor: '#ef4444', padding: '0.75rem 2rem', fontSize: '1.1rem', fontWeight: 600 }}
          onClick={() => setShowShiftClose(true)}
        >
          Shift Close
        </button>
      </div>

      {/* Transaction Table */}
      <div className="dash-card">
        <div className="card-header">
          <h3>Transaction History</h3>
        </div>
        <div className="table-responsive" style={{ border: 'none', background: 'transparent' }}>
          <table className="mobile-cards">
            <thead>
              <tr>
                <th>Record Info</th>
                <th>Customer</th>
                <th>Vehicle Details</th>
                <th>Amount</th>
                <th style={{ minWidth: '100px', paddingRight: '1rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {todaysRecords.length > 0 ? (
                todaysRecords.map(record => {
                  const editable = role === 'admin' ? true : (!isShiftClosed && isEditable(record.created_at));
                  const timeLeft = role === 'admin' ? null : getTimeLeft(record.created_at);
                  return (
                    <tr key={`${record.category}-${record.id}`}>
                      <td data-label="Record Info">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          <span className={`badge ${record.category === 'PUC' ? 'badge-diesel' : (record.type === 'PUC Reprint' ? 'badge-cng' : 'badge-petrol')}`} style={{ width: 'fit-content' }}>
                            {record.category === 'PUC' ? 'PUC' : (record.type || 'Number Link')}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                            {new Date(record.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </td>
                      <td data-label="Customer">
                        <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>{record.category === 'PUC' ? (record.customer_name || '-') : record.person_name}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{record.mobile_number || 'No Number'}</div>
                      </td>
                      <td data-label="Vehicle Details">
                        <div style={{ fontWeight: 700, letterSpacing: '0.5px' }}>{record.vehicle_number ? record.vehicle_number.toUpperCase() : '-'}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {record.vehicle_type && <span>{record.vehicle_type}</span>}
                          {record.fuel_type && <span style={{ color: `var(--${record.fuel_type === 'petrol' ? 'primary' : record.fuel_type === 'diesel' ? 'primary-hover' : 'text-main'})` }}>• {record.fuel_type}</span>}
                          {record.puc_expire_tenure && <span>• {record.puc_expire_tenure}M</span>}
                        </div>
                      </td>
                      <td data-label="Amount">
                        <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '1.1rem' }}>₹{Number(record.amount).toFixed(2)}</div>
                        <div style={{ marginTop: '0.25rem', textTransform: 'capitalize' }}>
                          <span className={`badge ${record.payment_method === 'paytm' ? 'badge-cng' : 'badge-petrol'}`}>
                            {record.payment_method || 'cash'}
                          </span>
                        </div>
                      </td>
                      <td data-label="Actions" style={{ paddingRight: '1rem' }}>
                        {editable ? (
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <button 
                              className="action-btn edit-btn"
                              title={timeLeft ? `Edit (${timeLeft} left)` : "Edit Record"}
                              onClick={() => setEditRecord(record)}
                            >
                              <Pencil size={14} />
                            </button>
                            <button 
                              className="action-btn delete-btn"
                              title={timeLeft ? `Delete (${timeLeft} left)` : "Delete Record"}
                              onClick={() => handleDelete(record)}
                            >
                              <Trash2 size={14} />
                            </button>
                            {timeLeft && <span className="time-badge">{timeLeft}</span>}
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                            <Lock size={13} /> {isShiftClosed ? 'Locked (Shift Closed)' : 'Locked (Time Expired)'}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                    No collections recorded today yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DailyBook;
